/**
 * Design Tokens - Single source of truth for all UI sizing, spacing, colors, and typography.
 *
 * RULES:
 * - All modals, buttons, cards, and panels must reference these tokens.
 * - Never hardcode font sizes, radii, spacing, or colors in component files.
 * - If a new value is needed, add it here first.
 *
 * FONT FAMILIES:
 * - Headings / labels / buttons: "Press Start 2P", monospace  (pixel art feel)
 * - Body text / descriptions / stats: system-ui, -apple-system, sans-serif  (readable)
 *
 * COLOR PALETTE:
 * - All variant colors (primary, secondary, success, warning, danger, info)
 *   match the SCSS button system in _buttons.scss.
 */

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const FONT = {
  /** Pixel art font for headings, buttons, labels, badges */
  heading: '"Press Start 2P", monospace',
  /** System font for body text, descriptions, stat values */
  body: 'system-ui, -apple-system, sans-serif',
} as const;

export const FONT_SIZE = {
  /** Modal title (desktop) */
  modalTitle: '20px',
  /** Modal title (mobile) */
  modalTitleMobile: '16px',
  /** Modal subtitle */
  modalSubtitle: '10px',
  /** Section heading inside modal body (e.g. "Description", "Pro Tip") */
  sectionHeading: '12px',
  /** Body / description text */
  body: '12px',
  /** Small body text / captions */
  caption: '10px',
  /** Extra-small (badges, micro labels) */
  micro: '8px',
  /** Stat value (large number) */
  statValue: '16px',
  /** Stat label */
  statLabel: '10px',
  /** Button text (normal) */
  buttonNormal: '12px',
  /** Button text (small) */
  buttonSmall: '10px',
  /** Button text (large) */
  buttonLarge: '14px',
  /** Countdown timer number */
  countdown: '16px',
  /** Countdown label */
  countdownLabel: '10px',
  /** HUD pill text */
  hudPill: '13px',
  /** HUD pill value */
  hudValue: '14px',
  /** Nav bar label */
  navLabel: '8px',
  /** Profile name (large) */
  profileName: '24px',
  /** Profile username */
  profileUsername: '16px',
} as const;

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------
export const SPACING = {
  /** Tiny gap between closely related items */
  xs: '4px',
  /** Small gap (icon-to-label, inline elements) */
  sm: '8px',
  /** Default gap (between cards, between sections) */
  md: '12px',
  /** Comfortable gap (between modal sections) */
  lg: '16px',
  /** Generous gap (between major groups) */
  xl: '20px',
  /** Extra generous (modal padding desktop) */
  xxl: '24px',
  /** Large modal padding desktop */
  xxxl: '32px',
  /** Section/region padding */
  section: '40px',
} as const;

// ---------------------------------------------------------------------------
// Border Radius
// ---------------------------------------------------------------------------
export const RADIUS = {
  /** Tiny elements (badges, pills, difficulty bars) */
  xs: '2px',
  /** Small elements (badges, pills) */
  sm: '6px',
  /** Buttons, cards, input fields */
  md: '10px',
  /** Panels, stat cards, info boxes */
  lg: '12px',
  /** Large containers, modal body sections */
  xl: '14px',
  /** Modal shell */
  modal: '15px',
  /** Nav buttons (top-rounded) */
  navButton: '16px 16px 0 0',
  /** HUD pill */
  pill: '20px',
  /** Full round (circular icons, avatars) */
  full: '50%',
} as const;

// ---------------------------------------------------------------------------
// Colors - Semantic palette
// ---------------------------------------------------------------------------
export const COLOR = {
  // -- Variant accent colors (match SCSS _buttons.scss) --
  primary: '#4ecdc4',
  primaryBorder: '#2c8f8a',
  secondary: '#45b7d1',
  secondaryBorder: '#2980b9',
  success: '#27ae60',
  successBorder: '#1e7e4a',
  successLight: '#48bb78',
  warning: '#f39c12',
  warningBorder: '#d35400',
  warningLight: '#ed8936',
  danger: '#e74c3c',
  dangerBorder: '#922b21',
  dangerLight: '#f56565',
  info: '#9b59b6',
  infoBorder: '#7d3c98',

  // -- Neutral text --
  textPrimary: '#e2e8f0',
  textSecondary: '#a0aec0',
  textMuted: '#718096',
  textWhite: '#ffffff',

  // -- Resource colors --
  coin: '#f39c12',
  coinLight: '#ffb703',
  gem: '#9b59b6',
  gemLight: '#c084fc',
  life: '#e74c3c',
  lifeLight: '#ff6b6b',
  star: '#f59e0b',
  starGold: '#ffd600',
  score: '#ffd166',
  streak: '#ff9f43',

  // -- Stat card accent colors --
  statRed: '#f56565',
  statOrange: '#ed8936',
  statBlue: '#4299e1',
  statYellow: '#f59e0b',
  statGreen: '#48bb78',

  // -- Difficulty colors --
  difficultyEasy: '#27ae60',
  difficultyMedium: '#f39c12',
  difficultyHard: '#e74c3c',

  // -- Backgrounds --
  /** Subtle tinted card BG (use with 0.1 alpha of accent) */
  cardBgAlpha: 0.1,
  /** Subtle tinted card border (use with 0.3 alpha of accent) */
  cardBorderAlpha: 0.3,
  /** Subtle overlay for HUD elements */
  hudOverlay: 'rgba(0, 0, 0, 0.5)',
  /** Glassmorphism overlay */
  glassOverlay: 'rgba(0, 0, 0, 0.35)',
  /** Glassmorphism border */
  glassBorder: 'rgba(255, 255, 255, 0.12)',
  /** Expanded overlay */
  expandedOverlay: 'rgba(0, 0, 0, 0.7)',
  /** Dark overlay for countdown / muted sections */
  surfaceMuted: 'rgba(255, 255, 255, 0.05)',
  /** Interactive surface hover */
  surfaceHover: 'rgba(255, 255, 255, 0.07)',
  /** Card surface (secondary) */
  surfaceCard: 'rgba(255, 255, 255, 0.05)',
  /** Highlighted card surface */
  surfaceHighlight: 'rgba(72, 187, 120, 0.1)',
  /** Footer / bottom bar bg */
  surfaceDark: '#0a0e13',
  /** Backdrop overlay */
  backdropOverlay: 'rgba(0, 0, 0, 0.8)',

  // -- Button inline colors (for modals that render buttons inline) --
  buttonGreen: '#48bb78',
  buttonGreenHover: '#38a169',
  buttonBlue: '#4299e1',
  buttonBlueHover: '#3182ce',
  buttonGray: '#718096',
  buttonGrayHover: '#4a5568',
  buttonAmber: '#f59e0b',
  buttonAmberHover: '#d97706',

  // -- Action button colors (for HUD/pause menu) --
  actionResume: '#4ade80',
  actionRestart: '#facc15',
  actionQuit: '#f87171',

  // -- Disabled state --
  disabled: '#777777',
  disabledOverlay: 'rgba(0, 0, 0, 0.5)',

  // -- Close / neutral buttons --
  closeButton: '#7f8c8d',
  closeButtonBorder: '#566573',
  closeButtonHover: '#95a5a6',
} as const;

// ---------------------------------------------------------------------------
// Variant color map (for Modal shell and components that vary by variant)
// ---------------------------------------------------------------------------
export interface VariantColors {
  primary: string;
  border: string;
  bgGradient: string;
}

const MOBILE_NAV_GRADIENT = 'linear-gradient(180deg, #111827 0%, #0a0e13 100%)';

export const VARIANT_COLORS: Record<string, VariantColors> = {
  default: {
    primary: '#4a5568',
    border: '#2d3748',
    bgGradient: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
  },
  primary: {
    primary: COLOR.primary,
    border: COLOR.primaryBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.primary} 0%, ${COLOR.secondary} 100%)`,
  },
  secondary: {
    primary: COLOR.secondary,
    border: COLOR.secondaryBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.secondary} 0%, #3498db 100%)`,
  },
  success: {
    primary: COLOR.success,
    border: COLOR.successBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.success} 0%, #2ecc71 100%)`,
  },
  warning: {
    primary: COLOR.warning,
    border: COLOR.warningBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.warning} 0%, #e67e22 100%)`,
  },
  danger: {
    primary: COLOR.danger,
    border: COLOR.dangerBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.danger} 0%, #c0392b 100%)`,
  },
  info: {
    primary: COLOR.info,
    border: COLOR.infoBorder,
    bgGradient: `linear-gradient(135deg, ${COLOR.info} 0%, #8e44ad 100%)`,
  },
} as const;

/** Get variant colors; for mobile, overrides bgGradient to the standard mobile nav gradient. */
export function getVariantColors(variant: string, isMobile: boolean): VariantColors {
  const colors = VARIANT_COLORS[variant] || VARIANT_COLORS.default;
  if (isMobile) {
    return { ...colors, bgGradient: MOBILE_NAV_GRADIENT };
  }
  return colors;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export const LAYOUT = {
  /** Max width for homescreen action buttons */
  homeButtonMaxWidth: 320,
} as const;

// ---------------------------------------------------------------------------
// Modal size configuration
// ---------------------------------------------------------------------------
export const MODAL_SIZE = {
  small: { maxWidth: '480px', padding: SPACING.xxl },
  medium: { maxWidth: '640px', padding: SPACING.xxxl },
  large: { maxWidth: '800px', padding: SPACING.section },
  fullscreen: { maxWidth: '100%', padding: SPACING.xxl },
} as const;

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------
export const SHADOW = {
  /** Text shadow for headings inside modals */
  text: '2px 2px 0 rgba(0, 0, 0, 0.8)',
  /** Card / panel shadow */
  card: '0 4px 12px rgba(0, 0, 0, 0.3)',
  /** Button shadow */
  button: '0 6px 20px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.2)',
  /** Modal shell shadow */
  modal: '0 25px 80px rgba(0, 0, 0, 0.6), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
  /** Close button shadow */
  closeButton: '0 6px 20px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.2)',
  /** Close button hover shadow */
  closeButtonHover: '0 12px 30px rgba(0, 0, 0, 0.4), inset 0 3px 6px rgba(255, 255, 255, 0.3)',
  /** Mobile back button shadow */
  mobileBack: '0 4px 15px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2)',
  /** Nav button shadow */
  navButton: '0 7px 40px rgba(0, 0, 0, 0.6), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
} as const;

// ---------------------------------------------------------------------------
// Blur
// ---------------------------------------------------------------------------
export const BLUR = {
  /** Standard glassmorphism blur */
  glass: 'blur(8px)',
  /** Modal backdrop blur */
  backdrop: 'blur(10px)',
  /** Expanded overlay blur */
  overlay: 'blur(12px)',
} as const;

// ---------------------------------------------------------------------------
// Z-Index layers
// ---------------------------------------------------------------------------
export const Z_INDEX = {
  /** Base game board */
  gameBoard: 100,
  /** Character layer */
  character: 1000,
  /** HUD elements */
  hud: 500,
  /** Powerup display */
  powerups: 800,
  /** Mobile navigation */
  mobileNav: 1000,
  /** Modal backdrop */
  modalBackdrop: 2499,
  /** Modal content */
  modal: 2500,
  /** Pause/expanded overlay */
  pauseOverlay: 5000,
  /** Commentary/toast */
  commentary: 9999,
} as const;

// ---------------------------------------------------------------------------
// Touch targets
// ---------------------------------------------------------------------------
export const TOUCH = {
  /** Minimum touch target size (WCAG) */
  minTarget: '44px',
  /** Mobile back button */
  backButton: '40px',
  /** Nav button minimum height */
  navMinHeight: '64px',
} as const;

// ---------------------------------------------------------------------------
// Modal inline-button style helper
// ---------------------------------------------------------------------------

/** Standard inline action button style for use inside modals.
 *  Use this instead of ad-hoc inline styles on motion.button. */
export function modalButtonStyle(color: string): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    padding: `${SPACING.md} ${SPACING.lg}`,
    minHeight: TOUCH.minTarget,
    backgroundColor: color,
    border: 'none',
    borderRadius: RADIUS.md,
    color: COLOR.textWhite,
    fontSize: FONT_SIZE.body,
    fontWeight: 'bold',
    cursor: 'pointer',
    fontFamily: FONT.body,
    boxSizing: 'border-box',
  };
}

/** Standard stat card style for use inside modals. */
export function statCardStyle(accentColor: string): React.CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${accentColor} 10%, transparent)`,
    padding: SPACING.md,
    borderRadius: RADIUS.lg,
    border: `2px solid color-mix(in srgb, ${accentColor} 30%, transparent)`,
    textAlign: 'center',
  };
}

/** Standard info row style (icon + text side-by-side). */
export function infoRowStyle(accentColor: string): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md,
    padding: SPACING.md,
    backgroundColor: `color-mix(in srgb, ${accentColor} 10%, transparent)`,
    borderRadius: RADIUS.lg,
    border: `2px solid color-mix(in srgb, ${accentColor} 30%, transparent)`,
  };
}

/** Countdown bar style. */
export function countdownStyle(color: string): React.CSSProperties {
  return {
    textAlign: 'center',
    padding: `${SPACING.sm} ${SPACING.md}`,
    backgroundColor: COLOR.surfaceMuted,
    borderRadius: RADIUS.sm,
    border: `2px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
  };
}

/** Glassmorphism pill style (HUD pills, home stats summary). */
export function glassPillStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md,
    padding: `${SPACING.sm} ${SPACING.md}`,
    borderRadius: RADIUS.pill,
    border: `1.5px solid ${COLOR.glassBorder}`,
    background: COLOR.glassOverlay,
    backdropFilter: BLUR.glass,
    WebkitBackdropFilter: BLUR.glass,
    color: COLOR.textWhite,
    cursor: 'pointer',
    fontSize: FONT_SIZE.hudPill,
    fontWeight: 500,
  };
}

/** Action button style for expanded/pause overlays. */
export function actionButtonStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.md,
    padding: `${SPACING.md} ${SPACING.xl}`,
    borderRadius: RADIUS.lg,
    border: `1.5px solid rgba(255, 255, 255, 0.15)`,
    background: COLOR.surfaceHover,
    color: COLOR.textWhite,
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.02em',
    width: '100%',
  };
}

/** Resource chip style (icon + value inline display). */
export function resourceChipStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.xs,
  };
}
